import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import "../styles/AuthCommon.css";
import "../styles/ElegantLogin.css";
import InputField from "../../../shared/components/ui/forms/InputField";
import PrimaryButton from "../../../shared/components/ui/buttons/PrimaryButton";
import { Heart, Crown, Sparkles, Mail, Lock, Shield, CheckCircle } from "lucide-react";
import { getBaseUrl } from "../../../config/urls";

/**
 * パスワードリセット確認ページ
 */
export default function ResetPasswordConfirm() {
  const location = useLocation();
  const navigate = useNavigate();
  const emailFromState = (location.state as any)?.email || "";

  const [email, setEmail] = useState(emailFromState);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const urlBase = getBaseUrl();
  const urlResetPasswordConfirm = urlBase + `/api/auth/reset-password-confirm`;

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validateCode = (code: string) => /^[0-9]+$/.test(code) && code.length === 6;
  const validatePassword = (pw: string) => pw.length >= 6;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) return setMessage("メールアドレスを入力してください。");
    if (!validateEmail(email)) return setMessage("正しいメールアドレスを入力してください。");
    if (!code) return setMessage("確認コードを入力してください。");
    if (!validateCode(code)) return setMessage("確認コードは数字のみで6文字を入力してください。");
    if (!newPassword) return setMessage("新しいパスワードを入力してください。");
    if (!validatePassword(newPassword)) return setMessage("パスワードは6文字以上で入力してください。");

    setLoading(true);
    setMessage("");

    try {
      const params = new URLSearchParams({ email, code, newPassword });
      const res = await fetch(`${urlResetPasswordConfirm}?${params.toString()}`, {
        method: "POST",
      });

      let data = {};
      try {
        data = await res.json();
      } catch (jsonErr) {
        console.error("JSON parse error:", jsonErr);
      }

      if (!res.ok) {
        if (res.status === 400 || res.status === 404) {
          setMessage((data as any).message || "入力内容に誤りがあります。");
        } else {
          setMessage("リセットに失敗しました。しばらくしてから再度お試しください。");
        }
        setLoading(false);
        return;
      }

      setMessage((data as any).message || "パスワードをリセットしました！");
      setResetSuccess(true);
    } catch (err) {
      console.error("Reset password confirm error:", err);
      setMessage("サーバーに接続できません。しばらくしてから再度お試しください。");
    } finally {
      setLoading(false);
    }
  };

  const handleLoginRedirect = () => {
    navigate("/auth/login");
  };

  return (
    <div className="elegant-login-container">
      <div className="elegant-login-background">
      </div>
      
      {!resetSuccess ? (
        <form onSubmit={handleSubmit} className="elegant-login-card">
          {/* ヘッダー装飾 */}
          <div className="elegant-login-header">
            <div className="header-decoration">
            </div>
            <h1 className="elegant-login-title">
              Photo Memorial
            </h1>
            <p className="elegant-login-subtitle">Wedding Memory Management</p>
          </div>

          {/* パスワードリセット確認フォーム */}
          <div className="elegant-form-section">
            <h2 className="elegant-form-title">パスワードをリセット</h2>
            <p className="elegant-forgot-description">
              メールで受信した確認コードと新しいパスワードを入力してください。
            </p>
            
            <div className="elegant-input-group">
              <div className="input-icon">
                <Mail size={20} />
              </div>
              <InputField
                type="email"
                placeholder="メールアドレス"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="elegant-input"
              />
            </div>

            <div className="elegant-input-group">
              <div className="input-icon">
                <Shield size={20} />
              </div>
              <InputField
                type="text"
                placeholder="確認コード（6桁）"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="elegant-input"
                maxLength={6}
              />
            </div>

            <div className="elegant-input-group">
              <div className="input-icon">
                <Lock size={20} />
              </div>
              <InputField
                type="password"
                placeholder="新しいパスワード"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="elegant-input"
              />
            </div>

            <PrimaryButton type="submit" disabled={loading} className="elegant-login-button">
              {loading ? (
                "送信中..."
              ) : (
                <>
                  <Lock size={20} />
                  パスワードリセット
                </>
              )}
            </PrimaryButton>

            {message && (
              <div className="elegant-message">
                <p>{message}</p>
              </div>
            )}

            <div className="elegant-links">
              <Link to="/auth/login" className="elegant-link">
                <span>ログインページに戻る</span>
              </Link>
            </div>
          </div>

          {/* フッター装飾 */}
          <div className="elegant-login-footer">
            <div className="footer-decoration">
              <div className="decoration-line"></div>
              <Heart className="footer-heart" />
              <div className="decoration-line"></div>
            </div>
            <p className="footer-text">Secure Password Reset for Special Moments</p>
          </div>
        </form>
      ) : (
        /* 成功画面 */
        <div className="elegant-login-card">
          {/* ヘッダー装飾 */}
          <div className="elegant-login-header">
            <div className="header-decoration">
            </div>
            <h1 className="elegant-login-title">
              Photo Memorial
            </h1>
            <p className="elegant-login-subtitle">Wedding Memory Management</p>
          </div>

          {/* 成功メッセージ */}
          <div className="elegant-form-section">
            <div className="elegant-success-section">
              <div className="success-icon">
                <CheckCircle size={60} className="success-check" />
              </div>
              <h2 className="elegant-form-title success-title">リセット完了</h2>
              <p className="elegant-forgot-description success-description">
                パスワードが正常にリセットされました。<br />
                新しいパスワードでログインしてください。
              </p>
              
              {message && (
                <div className="elegant-message success-message">
                  <p>{message}</p>
                </div>
              )}

              <PrimaryButton 
                type="button" 
                onClick={handleLoginRedirect} 
                className="elegant-login-button"
              >
                <>
                  <Heart size={20} />
                  ログインページへ
                </>
              </PrimaryButton>
            </div>
          </div>

          {/* フッター装飾 */}
          <div className="elegant-login-footer">
            <div className="footer-decoration">
              <div className="decoration-line"></div>
              <Heart className="footer-heart" />
              <div className="decoration-line"></div>
            </div>
            <p className="footer-text">Welcome Back to Your Special Moments</p>
          </div>
        </div>
      )}
    </div>
  );
}
